// Open Box API Handler - Debug Version

// Cache for collection data to avoid repeated API calls
const collectionCache = new Map();

// Format money for display
function formatMoney(price) {
  if (!price || price === 0) return '$0.00';
  
  // Convert to number first
  const priceNumber = parseFloat(price);
  if (isNaN(priceNumber)) return '$0.00';
  
  // Shopify API might return prices in dollars already, not cents
  // If price is less than 1000, assume it's already in dollars
  const priceInDollars = priceNumber < 1000 ? priceNumber : priceNumber / 100;
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(priceInDollars);
}

// Fetch all products from a collection (handles pagination)
async function fetchAllProductsFromCollection(collectionHandle) {
  // Check cache first
  if (collectionCache.has(collectionHandle)) {
    return collectionCache.get(collectionHandle);
  }

  let allProducts = [];
  let nextPage = `/collections/${collectionHandle}/products.json?limit=250`;
  
  try {
    while (nextPage) {
      const response = await fetch(nextPage);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      allProducts = allProducts.concat(data.products);
      
      // Check for next page
      const linkHeader = response.headers.get('Link');
      nextPage = linkHeader && linkHeader.includes('rel="next"') 
        ? linkHeader.match(/<([^>]+)>;\s*rel="next"/)[1].replace(window.location.origin, '')
        : null;
    }
    
    // Cache the results
    collectionCache.set(collectionHandle, allProducts);
    return allProducts;
    
  } catch (error) {
    return [];
  }
}

// Find matching open box products
function findMatchingOpenBoxProducts(allProducts, currentProductTitle, maxCount = 4) {
  const matchingProducts = [];
  let bestSavings = 0;
  
  for (const product of allProducts) {
    if (matchingProducts.length >= maxCount) break;
    
    if (product.title.includes(currentProductTitle)) {
      // Get price from first variant
      const firstVariant = product.variants && product.variants[0];
      const price = firstVariant ? firstVariant.price : 0;
      const compareAtPrice = firstVariant ? firstVariant.compare_at_price : 0;
      
      // Convert prices to numbers and calculate savings
      const priceNum = parseFloat(price) || 0;
      const compareAtPriceNum = parseFloat(compareAtPrice) || 0;
      const savings = compareAtPriceNum - priceNum;
      
      matchingProducts.push({
        ...product,
        price: price,
        compare_at_price: compareAtPrice,
        savings: savings
      });
      
      if (savings > bestSavings) {
        bestSavings = savings;
      }
    }
  }
  
  return { matchingProducts, bestSavings };
}

// Get top accessories by price
function getTopAccessories(allProducts, maxCount = 4) {
  const sortedProducts = allProducts
    .map(product => {
      // Get price from first variant
      const firstVariant = product.variants && product.variants[0];
      const price = firstVariant ? parseFloat(firstVariant.price) || 0 : 0;
      const compareAtPrice = firstVariant ? parseFloat(firstVariant.compare_at_price) || 0 : 0;
      const savings = compareAtPrice - price;
      
      return {
        ...product,
        price: price,
        compare_at_price: compareAtPrice,
        savings: savings
      };
    })
    .sort((a, b) => a.price - b.price)
    .slice(0, maxCount);
  
  return sortedProducts;
}

// Render products to DOM
function renderOpenBoxProducts(container, products, bestSavings, currentProductSavings, isAccessories = false) {
  if (!container) {
    return;
  }
  
  if (products.length === 0) {
    container.style.display = 'none';
    return;
  }
  
  // For products, only show if there are better savings
  if (!isAccessories && bestSavings < currentProductSavings) {
    container.style.display = 'none';
    return;
  }
  
  container.style.display = 'flex';
  
  const productsHTML = products.map(product => {
    // Calculate savings in dollars (assuming prices are already in dollars)
    const savings = Math.round(product.savings);
    const imageUrl = product.featured_image || product.images?.[0]?.src || '';
    
    return `
      <div class="open-box__product">
        <a class="open-box__product__img" href="${product.handle}"${isAccessories ? ' target="_blank"' : ''}>
          ${savings > 0 ? `<div class="open-box__product__img__save"><span>Save $${savings}</span></div>` : ''}
          <img
            alt="${product.title} image"
            loading="lazy"
            src="${imageUrl}"
            class="open-box__product__img__img"
          >
        </a>
        <div class="open-box__product__prices">
          ${product.compare_at_price ? `<span class="open-box__product__prices__orig-price"><del>${formatMoney(product.compare_at_price)}</del></span>` : ''}
          <span class="open-box__product__prices__price${savings > 0 ? '--sale' : ''}">${formatMoney(product.price)}</span>
        </div>
        <h3 class="open-box__product__title">
          <a href="${product.handle}"${isAccessories ? ' target="_blank"' : ''}>${product.title}</a>
        </h3>
      </div>
    `;
  }).join('');
  
  container.innerHTML = productsHTML;
}

// Show loading state
function showLoading(container) {
  if (!container) return;
  container.innerHTML = '<div class="open-box__loading">Loading open box products...</div>';
  container.style.display = 'block';
}

// Show error state
function showError(container, message) {
  if (!container) return;
  container.innerHTML = `<div class="open-box__error">${message}</div>`;
  container.style.display = 'block';
}

// Update tab title with savings information
function updateTabTitle(tabTitleElement, bestSavings, currentProductSavings) {
  if (!tabTitleElement || bestSavings < currentProductSavings) return;
  
  const savingsText = tabTitleElement.querySelector('.open-box__tab-title__savings');
  if (savingsText) {
    // Calculate additional savings in dollars (assuming prices are already in dollars)
    const additionalSavings = Math.round(bestSavings - currentProductSavings);
    savingsText.textContent = `Save $${additionalSavings}`;
    savingsText.style.display = 'inline';
  }
}

// Main function to initialize open box functionality
async function initializeOpenBox() {
  const openBoxContainer = document.querySelector('.open-box[data-open-box-products-collection]');
  if (!openBoxContainer) {
    return;
  }
  
  const config = {
    productsCollection: openBoxContainer.dataset.openBoxProductsCollection,
    accsCollection: openBoxContainer.dataset.openBoxAccsCollection,
    currentProductTitle: openBoxContainer.dataset.currentProductTitle,
    currentProductSavings: parseInt(openBoxContainer.dataset.currentProductSavings) || 0,
    showProducts: openBoxContainer.dataset.showProducts === 'true',
    showAccs: openBoxContainer.dataset.showAccs === 'true'
  };
  
  // Get containers
  const productsContainer = openBoxContainer.querySelector('.open-box__products-container');
  const accsContainer = openBoxContainer.querySelector('.open-box__accs-container');
  const tabTitleElement = openBoxContainer.querySelector('.open-box__tab-title');
  
      // Hide open box sections immediately
    hideEntireOpenBox(openBoxContainer);
    
    // Also hide individual sections immediately
    const productsSection = openBoxContainer.querySelector('.t4s-product-tabs-wrapper');
    if (productsSection) {
      productsSection.style.display = 'none';
    }
    const accsSection = openBoxContainer.querySelector('.open-box__accs');
    if (accsSection) {
      accsSection.style.display = 'none';
    }
    
      // Track if sections have content
  let productsHaveContent = false;
  let accsHaveContent = false;
  
  try {
    // Fetch products in parallel
    const promises = [];
    if (config.showProducts && config.productsCollection) {
      promises.push(fetchAllProductsFromCollection(config.productsCollection));
    }
    if (config.showAccs && config.accsCollection) {
      promises.push(fetchAllProductsFromCollection(config.accsCollection));
    }
    
    if (promises.length === 0) {
      // No collections configured, keep hidden
      return;
    }
    
    const results = await Promise.all(promises);
    
    // Process products
    if (config.showProducts && productsContainer && results[0]) {
      const allProducts = results[0];
      
      if (allProducts.length > 0) {
        const { matchingProducts, bestSavings } = findMatchingOpenBoxProducts(
          allProducts, 
          config.currentProductTitle
        );
        
        if (matchingProducts.length > 0 && bestSavings >= config.currentProductSavings) {
          renderOpenBoxProducts(productsContainer, matchingProducts, bestSavings, config.currentProductSavings, false);
          updateTabTitle(tabTitleElement, bestSavings, config.currentProductSavings);
          // Show the products section
          const productsSection = openBoxContainer.querySelector('.t4s-product-tabs-wrapper');
          if (productsSection) {
            productsSection.style.display = 'block';
          }
          productsHaveContent = true;
        }
      }
    }
    
    // Process accessories
    if (config.showAccs && accsContainer && results[config.showProducts ? 1 : 0]) {
      const allAccs = results[config.showProducts ? 1 : 0];
      
      if (allAccs.length > 0) {
        const topAccs = getTopAccessories(allAccs);
        
        // Check if accessories actually have savings or are worth showing
        const accessoriesWithSavings = topAccs.filter(acc => acc.savings > 0);
        
        if (accessoriesWithSavings.length > 0) {
          renderOpenBoxProducts(accsContainer, accessoriesWithSavings, 0, 0, true);
          // Show the accessories section
          const accsSection = openBoxContainer.querySelector('.open-box__accs');
          if (accsSection) {
            accsSection.style.display = 'block';
          }
          accsHaveContent = true;
        }
      }
    }
    
    // Show the entire open box if either section has content
    if (productsHaveContent || accsHaveContent) {
      showEntireOpenBox(openBoxContainer);
    }
    
  } catch (error) {
    // Keep hidden on error
    console.error('Error loading open box products:', error);
  }
}

// Helper function to hide the entire open box section
function hideEntireOpenBox(openBoxContainer) {
  // Hide the main open box container
  if (openBoxContainer) {
    openBoxContainer.style.display = 'none';
  }
  
  // Hide the open box title
  const openBoxTitle = document.querySelector('.open-box__title');
  if (openBoxTitle) {
    openBoxTitle.style.display = 'none';
  }
}

// Helper function to show the entire open box section
function showEntireOpenBox(openBoxContainer) {
  // Show the main open box container
  if (openBoxContainer) {
    openBoxContainer.style.display = 'block';
  }
  
  // Show the open box title
  const openBoxTitle = document.querySelector('.open-box__title');
  if (openBoxTitle) {
    openBoxTitle.style.display = 'block';
  }
}

// Initialize when DOM is ready (jQuery compatible)
if (typeof jQuery_T4NT !== 'undefined') {
  jQuery_T4NT(document).ready(function($) {
    initializeOpenBox();
  });
} else {
  // Fallback for non-jQuery environments
  document.addEventListener('DOMContentLoaded', function() {
    initializeOpenBox();
  });
}

// Also try immediate initialization in case DOM is already ready
if (document.readyState === 'loading') {
  // Document still loading, will wait for ready event
} else {
  initializeOpenBox();
}

// Export for potential external use
window.initializeOpenBox = initializeOpenBox; 